import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, of } from 'rxjs';
import { EntitlementsLoadedService } from './entitlements-loaded.service';
import { EnvironmentService } from './environment.service';
import { LoginService } from '@core/services/login.service';
import { Router } from '@angular/router';
import { StorageService } from './storage.service';
import { paths } from '@env/paths';
import { entitlements } from '@env/entitlements';

@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  user;
  isSessionValid: boolean;

  constructor(
    private readonly http: HttpClient,
    private readonly entitlementsLoadedService: EntitlementsLoadedService,
    private readonly environmentService: EnvironmentService,
    private readonly loginService: LoginService,
    private readonly router: Router,
    private readonly localStorageService: StorageService,
    private readonly storageService: StorageService
  ) {}

  navigateToLoginRedirect(): void {
    // Capturing the vin and storing in local storage to use later for experttool
    const queryParams = new URLSearchParams(window.location.search);
    const vin = queryParams.get('vin');
    if (vin) {
      localStorage.setItem('vin', vin);
    }
    console.log('Redirecting to login...');
    window.location.href = `${environment.commonApiUrl}login-redirect`;
  }
  
  checkSessionValidity(): boolean {
    const loginProgressValidTill = this.localStorageService.getItem(
      'fcsc-access-valid-till'
    );
    return loginProgressValidTill && loginProgressValidTill > new Date().getTime();
  }

  initialize() {
    this.environmentService.setEnvironmentUrlViaSubdomain();

    const navigateToExpertTool = () => {
      const queryParams = new URLSearchParams(window.location.search);
      const vin = queryParams.get('vin');
      if (vin) {
        localStorage.setItem('vin', vin);
      }
      const currentPath = window.location.pathname;
      if (currentPath === '/oauth2/idpresponse') {
      this.router.navigate([
        `${paths.fcsc.experttool.root}/${paths.fcsc.experttool.flashware}`,
      ]);}
      // this.router.navigate([
      //   `${paths.fcsc.experttool.root}/${paths.fcsc.experttool.flashware}`,
      // ]);
    };

    const handleUser = (user, resolve) => {
      this.user = this.setUser(user);
      if (this.hasUiAccess()) {
        this.entitlementsLoadedService.emitEntitlementsLoadedEvent(true);
        this.entitlementsLoadedService.emitUsernameLoadedEvent(true);
        this.loginService.startRefreshAccessTokenInterval();
        navigateToExpertTool();
        resolve(true);
      } else {
        console.log('User does not have UI access — redirecting to forbidden page.');
        this.router.navigate([paths.common.forbidden], { queryParams: { reason: 'expired' } });
        resolve(false);
      }
    };

    const handleJwtError = (error, resolve) => {
      console.error('jwt-echo error:', error);
      this.router.navigate([paths.common.forbidden], { queryParams: { reason: 'expired' } });
      resolve(false);
    };

    return new Promise(resolve => {
      const hostname = window?.location?.hostname;
      if (hostname !== 'localhost') {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('code') || '';
        const processJwtEcho = () => {
          this.http.get<any>(`${environment.commonApiUrl}jwt-echo`).subscribe({
            next: user => handleUser(user, resolve),
            error: error => handleJwtError(error, resolve)
          });
        };
        if (token.length) {
          this.loginService.getAccessToken(token).subscribe({
            next: (response: any) => {
              if (response?.access_token) {
                this.loginService.setResponseDataToLocalStorage(response);
                this.storageService.removeItem('loginProgressValidTill');
                this.loginService.startRefreshAccessTokenInterval();
                this.isSessionValid = this.checkSessionValidity();
                if (localStorage.getItem('fcsc-access-token')?.length && this.isSessionValid) {
                  processJwtEcho();
                } else {
                  console.log('No valid token found or session is expired');
                  this.navigateToLoginRedirect();
                }
              } else {
                this.router.navigate([paths.common.forbidden]);
              }
            },
            error: error => {
              if (error.message === 'MFA required!' || (error.message && error.message.split(' ')[1] === 'MFA required!')) {
                this.navigateToLoginRedirect();
              }
            },
          });
        } else {
          this.isSessionValid = this.checkSessionValidity();
          if (localStorage.getItem('fcsc-access-token')?.length && this.isSessionValid) {
            processJwtEcho();
            //navigateToExpertTool();
          } else {
            console.log('No valid token found or session is expired');
            this.navigateToLoginRedirect();
          }
        }
      } else {
        this.localStorageService.setItem('fcsc-access-token', environment.accessToken);
        this.localStorageService.setItem('fcsc-access-valid-till', new Date().getTime() + 12000);
        this.localStorageService.setItem('fcsc-access-expires-in', 1800);
        this.localStorageService.setItem('fcsc-access-refresh-token', environment.refreshToken);
        this.getJwtMock().subscribe({
          next: user => handleUser(user, resolve),
          error: error => handleJwtError(error, resolve)
        });
      }
    });
  }

  private setUser(user) {
    return {
        upn: user.upn,
        name: {
            givenName: user.name.givenName,
            familyName: user.name.familyName,
        },
        email: user.email,
        groups: user.groups || [],
        permissions: user.permissions || [],
    };
  }

  public getEntitlements(): string[] {
    if (this.user && this.user.permissions) {
      return this.user.permissions;
    } else {
      return [];
    }
  }

  /**
   * Checks whether the current user is entitled to access the application at all.
   * This is the initial, app-wide access gate and is independent of any
   * feature-specific entitlement checks performed elsewhere (e.g. route guards).
   */
  public hasUiAccess(): boolean {
    return this.getEntitlements().indexOf(entitlements.VUS.FCSC_UI_ACCESS) > -1;
  }

  public isLoggedIn(): boolean {
    return this.user !== undefined && this.user !== null;
  }

  public logout() {
    console.log(`${environment.commonApiUrl}/logout`);
    window.location.href = `${environment.commonApiUrl}logout`;
  }

  private getJwtMock(): Observable<any> {
    let JWTUserMock = {
            upn: 'PIDC525',
            name: {
                givenName: 'Cypress',
                familyName: 'User',
            },
            email: 'PIDC525@mb.com',
            groups: [
              "VUS.RadioCode",
              "VUS.ZKE_Pkw_Help",
              "VUS.VPOLKW_ImageVorabDelete",
              "VUS.VPOLKW_DataTestExport",
              "VUS.VPOLKW_Help",
              "VUS.EcuInfo",
              "VUS.VPOLKW_ImageSerienDeploy",
              "VUS.VPOLKW_CalcExport",
              "VUS.VPOLKW_ImageReadOnly",
              "VUS.VPOLKW_DataVorabDelete",
              "VUS.VPOLKW_ImageTestDelete",
              "VUS.KeyCalculation",
              "VUS.ImmobilizerCode",
              "VUS.VPOLKW_SerienDataExport",
              "VUS.VPOLKW_ImageVorabBuild",
              "VUS.VPOLKW_DataVorabImport",
              "VUS.VPOLKW_Logging",
              "VUS.DocumentEcu",
              "VUS.ExecuteXFunction",
              "VUS.VPOLKW_DataTestDelete",
              "VUS.VPOLKW_DataTestImport",
              "VUS.CalculateFmmEraseUnlockKey",
              "VUS.VPOLKW_ImageTestBuild",
              "VUS.VehicleInformation",
              "VUS.NissanPin",
              "VUS.DocumentRetrofitCode",
              "VUS.VPOLKW_DataVorabExport",
              "VUS.VPOLKW_ImageVorabDeploy",
              "VUS.VPOLKW_Actuator",
              "VUS.VPOLKW_FlashwareCalculation",
              "VUS.DocumentVehicle",
              "VUS.VPOLKW_ImageSerienDelete",
              "VUS.VPOLKW_ImageSerienRelease",
              "VUS.VPOLKW_Expertentool",
              "VUS.CertificateCalculation",
              "VUS.VPOLKW_DataSetDownload",
              "VUS.FCSC_PUBLIC",
              "VUS.FlashwareCalculation",
              "VUS.FCSC_ManagementData",
              "VUS.FCSC_FinlogCrypto",
              "VUS.FCSC_VorabData",
              "VUS.FCSC_SerienData",
              "VUS.FCSC_OpenSearchDashboard",
              "VUS.FCSC_UIAccess"
            ],
            permissions: [
              "VUS.RadioCode",
              "VUS.ZKE_Pkw_Help",
              "VUS.VPOLKW_ImageVorabDelete",
              "VUS.VPOLKW_DataTestExport",
              "VUS.VPOLKW_Help",
              "VUS.EcuInfo",
              "VUS.VPOLKW_ImageSerienDeploy",
              "VUS.VPOLKW_CalcExport",
              "VUS.VPOLKW_ImageReadOnly",
              "VUS.VPOLKW_DataVorabDelete",
              "VUS.VPOLKW_ImageTestDelete",
              "VUS.KeyCalculation",
              "VUS.ImmobilizerCode",
              "VUS.VPOLKW_SerienDataExport",
              "VUS.VPOLKW_ImageVorabBuild",
              "VUS.VPOLKW_DataVorabImport",
              "VUS.VPOLKW_Logging",
              "VUS.DocumentEcu",
              "VUS.ExecuteXFunction",
              "VUS.VPOLKW_DataTestDelete",
              "VUS.VPOLKW_DataTestImport",
              "VUS.CalculateFmmEraseUnlockKey",
              "VUS.VPOLKW_ImageTestBuild",
              "VUS.VehicleInformation",
              "VUS.NissanPin",
              "VUS.DocumentRetrofitCode",
              "VUS.VPOLKW_DataVorabExport",
              "VUS.VPOLKW_ImageVorabDeploy",
              "VUS.VPOLKW_Actuator",
              "VUS.VPOLKW_FlashwareCalculation",
              "VUS.DocumentVehicle",
              "VUS.VPOLKW_ImageSerienDelete",
              "VUS.VPOLKW_ImageSerienRelease",
              "VUS.VPOLKW_Expertentool",
              "VUS.CertificateCalculation",
              "VUS.VPOLKW_DataSetDownload",
              "VUS.FCSC_PUBLIC",
              "VUS.FlashwareCalculation",
              "VUS.FCSC_ManagementData",
              "VUS.FCSC_FinlogCrypto",
              "VUS.FCSC_VorabData",
              "VUS.FCSC_SerienData",
              "VUS.FCSC_OpenSearchDashboard",
              "VUS.FCSC_UIAccess"
            ],
        };
    return of(JWTUserMock);
  }
}
